import { z } from 'zod';

import { ProfileId, SessionId, SessionType } from 'backend-client-web';

import { makeProtobufEnumSchema } from '../enumSchemas';
import { WithUnknownValuePlaceholder } from '../unknownValuePlaceholder';

import { SessionClientSettings } from './SessionClientSettings';

export const unlockTimeLimitSchema = z.object({
  enabled: z.boolean(),
  minutes: z.number(),
});

export interface Session {
  id: SessionId;
  createdAt: Date;
  lastUsedAt: Date;
  unlockedAt?: Date;
  unlockedUntil?: Date;
  unlockRequestedAt?: Date;
  unlockExtendAt?: Date;
  clientSettings?: SessionClientSettings;
  sessionType: WithUnknownValuePlaceholder<SessionType>;
  // if empty, all are enabled
  enabledProfileIds: ProfileId[];
  unlockTimeLimitMinutes: number;
}

export const Session = z
  .object({
    id: z.uuid(),
    createdAt: z.instanceof(Date),
    lastUsedAt: z.instanceof(Date),
    unlockedAt: z.instanceof(Date).optional(),
    unlockedUntil: z.instanceof(Date).optional(),
    unlockRequestedAt: z.instanceof(Date).optional(),
    unlockExtendAt: z.instanceof(Date).optional(),
    clientSettings: SessionClientSettings.optional(),
    sessionType: makeProtobufEnumSchema(SessionType, SessionType.UNKNOWN).default(
      SessionType.UNKNOWN,
    ),
    enabledProfileIds: z.uuid().array().default([]),
    unlockTimeLimit: unlockTimeLimitSchema.optional(),
    unlockTimeLimitMinutes: z.number().optional(),
  })
  .transform((session) => {
    const { unlockTimeLimit, ...s } = session;
    if (s.unlockTimeLimitMinutes !== undefined) {
      // Need to assign unlockTimeLimitMinutes explicitly so TS can prove it's not undefined
      return { ...s, unlockTimeLimitMinutes: s.unlockTimeLimitMinutes };
    }
    if (unlockTimeLimit && unlockTimeLimit.enabled) {
      return { ...s, unlockTimeLimitMinutes: unlockTimeLimit.minutes };
    }
    return {
      ...s,
      // This is the default for unlock time set in the backend migration
      unlockTimeLimitMinutes: 8 * 60,
    };
  });
